import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { useDismiss } from '../../lib/hooks'
import { btn } from '../ui'
import { choiceForKey, INSERT_SECTIONS, menuDigit, moveIndex, shortcutAria, shortcutLabel, withoutChoices, type InsertChoice, type InsertSection } from './insertChoices'

// The insert menu: the eight block types (10/2) as cards, two across (Andrew,
// 10/2: cards, not rows), Thought, Margin note and Image first, a quiet rule,
// then the rest; each card has its number (press it while the menu's open),
// its ⌥⌘ key chip and a line of hint. It opens from
// the round wine "+" beside the current empty line. The "/" menu
// (SlashMenu.tsx) borrows the panel for your thoughts.

export interface MenuOption {
  key: string
  label: ReactNode
  hint?: ReactNode
  /** The keyboard shortcut, shown in a key chip beside the label. */
  shortcut?: { label: string; aria: string }
  /** Spans both columns (thought results in the "/" menu). */
  wide?: boolean
  /** Serif label (a thought's own words rather than a block type). */
  serif?: boolean
  /** Its number in the menu, pressed to pick it (the "+" menu). */
  digit?: number
}

export interface MenuGroup {
  id: string
  /** A small-caps header; without one, groups after the first are split by a quiet rule. */
  title?: string
  options: MenuOption[]
  /** Fill this many rows down the first column before the second. */
  stack?: number
}

/** The flat keyboard order of a menu's options. */
export function menuKeys(groups: MenuGroup[]): string[] {
  return groups.flatMap((g) => g.options.map((o) => o.key))
}

/** Groups for the block-type choices. */
export function choiceGroups(sections: InsertSection[] = INSERT_SECTIONS, apple?: boolean): MenuGroup[] {
  let digit = 0
  return sections.map((s) => ({
    id: s.id,
    title: s.title,
    stack: s.stack,
    options: s.items.map((c) => ({
      key: `c:${c.type}`,
      label: c.label,
      hint: c.hint,
      shortcut: { label: shortcutLabel(c.type, apple), aria: shortcutAria(c.type) },
      digit: ++digit <= 9 ? digit : undefined,
    })),
  }))
}

/**
 * A key chip, styled the way Burrow's search bar shows its ⌘K: a small
 * outlined box in the muted sans. Hidden on phones, which have no keys.
 */
export function KeyChip({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      aria-hidden
      className={`inline-flex shrink-0 pointer-coarse:hidden items-center gap-0.5 rounded-md border border-border/40 bg-muted/40 px-1.5 py-0.5 font-sans font-normal text-muted-foreground/60 ${className}`}
      style={{ fontSize: '0.65rem', lineHeight: 1.4 }}
    >
      {children}
    </kbd>
  )
}

/**
 * Arrow keys through the options as they sit on screen: the nearest option in
 * that direction. Falls back to the flat grid order when there's no layout to
 * read (tests) or nothing lies that way (it wraps).
 */
function spatialMove(key: string, menuId: string, keys: string[], index: number, columns: number): number | null {
  const fallback = moveIndex(key, index, keys.length, columns)
  if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(key) || index < 0) return fallback
  const rects = keys.map((k) => document.getElementById(optionId(menuId, k))?.getBoundingClientRect())
  const cur = rects[index]
  if (!cur || rects.every((r) => !r || (r.width === 0 && r.height === 0))) return fallback
  const cx = cur.left + cur.width / 2
  const cy = cur.top + cur.height / 2
  const vertical = key === 'ArrowDown' || key === 'ArrowUp'
  let best: number | null = null
  let bestScore = Infinity
  rects.forEach((r, i) => {
    if (!r || i === index) return
    const dx = r.left + r.width / 2 - cx
    const dy = r.top + r.height / 2 - cy
    const ahead = key === 'ArrowDown' ? dy > 2 : key === 'ArrowUp' ? dy < -2 : key === 'ArrowRight' ? dx > 2 : dx < -2
    if (!ahead) return
    // Along the arrow counts once; sideways counts heavily, so Down stays in its column.
    const score = vertical ? Math.abs(dy) + Math.abs(dx) * 4 : Math.abs(dx) + Math.abs(dy) * 4
    if (score < bestScore) {
      bestScore = score
      best = i
    }
  })
  return best ?? fallback
}

interface PanelProps {
  id: string
  /** The listbox's name (default "Insert"). */
  label?: string
  groups: MenuGroup[]
  active: string | null
  onChoose: (key: string) => void
  onHover: (key: string) => void
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void
  footer?: ReactNode
  className?: string
  panelRef?: React.Ref<HTMLDivElement>
  /** Focusable listbox (the "+" menu); the "/" menu leaves focus in the text. */
  focusable?: boolean
  /** One slim column, each option on a single line: number, label, hint, key chip (the "+" menu). */
  slim?: boolean
}

export const optionId = (menuId: string, key: string) => `${menuId}-${key.replace(/[^A-Za-z0-9_-]/g, '_')}`

export function InsertMenuPanel({ id, label = 'Insert', groups, active, onChoose, onHover, onKeyDown, footer, className = '', panelRef, focusable, slim = false }: PanelProps) {
  return (
    <div
      ref={panelRef}
      id={id}
      data-keep-selection
      role="listbox"
      aria-label={label}
      tabIndex={focusable ? -1 : undefined}
      aria-activedescendant={focusable && active ? optionId(id, active) : undefined}
      onKeyDown={onKeyDown}
      className={`z-30 flex ${slim ? 'w-[min(392px,calc(100vw-2rem))] p-1.5' : 'w-[min(540px,calc(100vw-2rem))] p-2'} flex-col rounded-2xl border border-border/50 bg-card font-sans text-foreground shadow-[var(--shadow-pop)] outline-none ${className}`}
    >
      {groups.map((g, gi) => (
        <div
          key={g.id}
          role="group"
          aria-labelledby={g.title ? `${id}-${g.id}` : undefined}
          className={slim ? 'flex flex-col gap-px' : 'grid grid-cols-1 gap-x-2.5 gap-y-0.5 sm:grid-cols-2'}
        >
          {g.title ? (
            <div
              id={`${id}-${g.id}`}
              className={`col-span-full px-2.5 pb-1 text-[10.5px] font-semibold tracking-[0.06em] text-muted-foreground ${gi === 0 ? 'pt-1.5' : 'pt-2.5'}`}
            >
              {g.title}
            </div>
          ) : (
            gi > 0 && <div role="separator" className="col-span-full mx-2.5 my-1 h-px bg-border/60" />
          )}
          {/* A stacked group fills down the first column first. */}
          <div
            className={g.stack && !slim ? 'contents sm:col-span-full sm:grid sm:grid-flow-col sm:grid-cols-2 sm:gap-x-2.5 sm:gap-y-0.5' : 'contents'}
            style={g.stack && !slim ? ({ gridTemplateRows: `repeat(${g.stack}, auto)` } as CSSProperties) : undefined}
          >
            {g.options.map((o) => {
              const on = o.key === active
              const labelCls = o.serif
                ? 'line-clamp-2 font-display text-[14px]/[1.45] text-foreground/85'
                : `text-[13px] font-medium ${on ? 'text-primary' : 'text-foreground'}`
              return (
                <div
                  key={o.key}
                  id={optionId(id, o.key)}
                  role="option"
                  aria-selected={on}
                  aria-keyshortcuts={o.shortcut?.aria}
                  // Keep the caret where it is; choose on click.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onChoose(o.key)}
                  onMouseEnter={() => onHover(o.key)}
                  className={`flex cursor-pointer rounded-lg text-left ${slim ? 'items-center gap-2.5 px-2.5 py-[7px]' : 'flex-col gap-0.5 px-2.5 py-2'} ${o.wide ? 'col-span-full' : ''} ${on ? 'bg-accent/15' : 'hover:bg-foreground/4'}`}
                >
                  {slim ? (
                    <>
                      {o.digit !== undefined && (
                        <span aria-hidden className={`w-2 shrink-0 text-center text-[11px] tabular-nums pointer-coarse:hidden ${on ? 'text-primary' : 'text-muted-foreground/60'}`}>
                          {o.digit}
                        </span>
                      )}
                      <span className={`shrink-0 ${labelCls}`}>{o.label}</span>
                      <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">{o.hint}</span>
                      {o.shortcut && <KeyChip>{o.shortcut.label}</KeyChip>}
                    </>
                  ) : (
                    <>
                      <span className="flex items-center justify-between gap-2">
                        <span className="flex items-baseline gap-2">
                          {o.digit !== undefined && (
                            <span aria-hidden className={`text-[11px] tabular-nums pointer-coarse:hidden ${on ? 'text-primary' : 'text-muted-foreground/60'}`}>
                              {o.digit}
                            </span>
                          )}
                          <span className={labelCls}>{o.label}</span>
                        </span>
                        {o.shortcut && <KeyChip>{o.shortcut.label}</KeyChip>}
                      </span>
                      {o.hint && <span className={`text-[11px] text-muted-foreground ${o.serif ? 'font-medium' : ''}`}>{o.hint}</span>}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}
      {footer && <div className={`px-2.5 pb-0.5 text-[11px] text-muted-foreground ${slim ? 'mt-1 border-t border-border/40 pt-1.5' : 'pt-1.5'}`}>{footer}</div>}
    </div>
  )
}

interface InsertMenuProps {
  onChoose: (choice: InsertChoice) => void
  /** Choices not on offer here (inside a column cell). */
  exclude?: ReadonlySet<InsertChoice>
}

/**
 * The round wine "+" beside the current empty line. It sits in the left
 * gutter when there's room and at the line's right end on phones; the menu
 * opens beside it (below it on phones).
 */
export function InsertMenu({ onChoose, exclude }: InsertMenuProps) {
  const menuId = useId()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(rootRef, open, close)

  const groups = choiceGroups(withoutChoices(INSERT_SECTIONS, exclude))
  const keys = menuKeys(groups)

  useEffect(() => {
    if (open) panelRef.current?.focus({ preventScroll: true })
  }, [open])

  const toggle = () => {
    if (open) {
      setOpen(false)
      return
    }
    setActive(keys[0])
    setOpen(true)
  }

  const choose = (key: string) => {
    setOpen(false)
    onChoose(key.slice(2) as InsertChoice)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // A block's own shortcut works with the menu open, too.
    const direct = choiceForKey(e)
    if (direct) {
      e.preventDefault()
      if (keys.includes(`c:${direct}`)) choose(`c:${direct}`)
      return
    }
    // A row's number picks it: 1 Thought, 2 Margin note, 3 Image…
    const digit = menuDigit(e)
    if (digit !== null) {
      e.preventDefault()
      if (keys[digit - 1]) choose(keys[digit - 1])
      return
    }
    const i = active ? keys.indexOf(active) : -1
    const next = spatialMove(e.key, menuId, keys, i, 2)
    if (next !== null) {
      e.preventDefault()
      setActive(keys[next])
      document.getElementById(optionId(menuId, keys[next]))?.scrollIntoView?.({ block: 'nearest' })
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (active) choose(active)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      buttonRef.current?.focus()
    } else if (e.key === 'Tab') {
      setOpen(false)
    }
  }

  return (
    <div
      ref={rootRef}
      data-keep-selection
      className="absolute top-0.5 z-20 @max-[760px]/wr-page:right-0 @min-[760px]/wr-page:-left-[38px]"
    >
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={open ? 'Close the insert menu' : 'Insert a block'}
        // Don't take focus from the text: the caret stays put.
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggle}
        className={`flex size-[26px] items-center justify-center rounded-full font-sans text-[18px] font-light leading-none transition-colors ${
          open ? 'bg-foreground text-card' : 'bg-accent text-accent-foreground hover:opacity-90'
        }`}
      >
        <span aria-hidden className={`transition-transform ${open ? 'rotate-45' : ''}`}>
          +
        </span>
      </button>
      {open && (
        <InsertMenuPanel
          id={menuId}
          panelRef={panelRef}
          focusable
          groups={groups}
          active={active}
          onChoose={choose}
          onHover={setActive}
          onKeyDown={onKeyDown}
          footer={
            <>
              <span className="pointer-coarse:hidden">1–{keys.length} or ↑↓ choose · ↵ insert · esc close · </span>/ for your thoughts
            </>
          }
          className="absolute @max-[760px]/wr-page:right-0 @max-[760px]/wr-page:top-[34px] @min-[760px]/wr-page:left-[38px] @min-[760px]/wr-page:top-0"
        />
      )}
    </div>
  )
}

export function LinkForm({ onAdd, onCancel }: { onAdd: (url: string) => void; onCancel: () => void }) {
  const [value, setValue] = useState('')
  return (
    <form
      data-keep-selection
      onSubmit={(e) => {
        e.preventDefault()
        if (value.trim()) onAdd(value)
      }}
      className="relative z-10 mb-[22px] flex flex-wrap gap-2 rounded-lg border border-border/60 bg-card p-2 font-sans"
    >
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && onCancel()}
        inputMode="url"
        placeholder="Paste a link"
        aria-label="Link for the card"
        className="min-w-0 flex-1 bg-transparent px-2 text-[13.5px] text-foreground outline-none"
      />
      <div className="flex gap-1">
        <button type="button" onClick={onCancel} className={btn.ghost}>
          Cancel
        </button>
        <button type="submit" className={btn.primary}>
          Add card
        </button>
      </div>
    </form>
  )
}
